import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, Phone, Mail, MapPin, Instagram, Youtube, Facebook } from 'lucide-react'
import Reveal from './Reveal'
import api from '../../services/api'

export default function CinematicFooter({ contactEmail, closing = true }) {
  const [primaryIntake, setPrimaryIntake] = useState(null)

  useEffect(() => {
    let cancelled = false
    api
      .get('/intakes/primary')
      .then(({ data }) => {
        if (!cancelled && data?.primary_intake) setPrimaryIntake(data.primary_intake)
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [])

  // Dynamic intake CTA text — never hardcoded. Falls back gracefully if no primary intake.
  const intakeName = primaryIntake?.name
  const regClose = primaryIntake?.registration_close_date
  const startDate = primaryIntake?.start_date
  const ctaLine = intakeName
    ? `${intakeName} applications close${
        regClose ? ` ${new Date(regClose).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })}` : ''
      }.${startDate ? ` Training starts ${new Date(startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })}.` : ''} Limited seats. Unlimited potential.`
    : 'Applications are open. Limited seats. Unlimited potential.'

  return (
    <>
      {closing && (
      <section className="relative py-20 sm:py-32 lg:py-64 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/Gallery/Academy/124414.jpg" alt="" className="w-full h-full object-cover opacity-15" />
          <div className="absolute inset-0 bg-gradient-to-b from-cinema-black via-cinema-black/95 to-cinema-black" />
        </div>
        <div className="cinema-glow-orb w-[800px] h-[400px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />

        <div className="cinema-container relative text-center">
          <Reveal>
            <span className="cinema-label">Your Chapter Starts Here</span>
            <h2 className="cinema-headline-xl mt-10 max-w-4xl mx-auto">
              Become part of<br />
              <span className="text-cinema-gold">Africa's creative future</span>
            </h2>
            <p className="mt-10 cinema-body-lg max-w-xl mx-auto text-cinema-soft/60">
              {ctaLine}
            </p>
            <div className="mt-14">
              <Link to="/register" className="cinema-btn-primary group">
                Start Your Application
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
      )}

      {/* Footer */}
      <footer className="border-t border-white/5 py-16 sm:py-20">
        <div className="cinema-container">
          <div className="grid sm:grid-cols-2 lg:grid-cols-[1fr_0.8fr_0.8fr_1.4fr_1fr] gap-12 lg:gap-20 xl:gap-24 mb-16">
            <div className="lg:col-span-1">
              <Link to="/explore" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="flex items-center gap-3 mb-6 group">
                <img src="/Logos/SMA LOGO  DARK MODE.png" alt="SMA" className="h-10 transition-transform duration-500 group-hover:scale-105" />
                <div>
                  <p className="font-display text-sm text-white">Synergy Multimedia</p>
                  <p className="text-xs uppercase tracking-wider text-cinema-gold font-medium">Academy</p>
                </div>
              </Link>
              <p className="text-sm text-cinema-muted font-sans leading-relaxed">
                A creative learning, production and technology platform. Kampala, Uganda.
              </p>
            </div>

            <div>
              <h4 className="text-xs uppercase tracking-wider text-cinema-gold mb-6">Explore</h4>
              <ul className="space-y-3 text-sm text-cinema-muted font-sans">
                <li><Link to="/programs" className="hover:text-white transition-colors">Programs</Link></li>
                <li><Link to="/creative-tools" className="hover:text-white transition-colors">Creative Tools</Link></li>
                <li><Link to="/project-lab" className="hover:text-white transition-colors">Project Lab</Link></li>
                <li><Link to="/masterclass" className="hover:text-white transition-colors">Masterclass</Link></li>
                <li><Link to="/showcase" className="hover:text-white transition-colors">Showcase</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-xs uppercase tracking-wider text-cinema-gold mb-6">Information</h4>
              <ul className="space-y-3 text-sm text-cinema-muted font-sans">
                <li><Link to="/advisor" className="hover:text-white transition-colors">Talk to an Advisor</Link></li>
                <li><Link to="/welcome" className="hover:text-white transition-colors">Welcome</Link></li>
                <li><Link to="/verify-certificate" className="hover:text-white transition-colors">Verify Certificate</Link></li>
                <li><Link to="/contact" className="hover:text-white transition-colors">Support</Link></li>
                <li><Link to="/register" className="hover:text-white transition-colors">Apply</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-xs uppercase tracking-wider text-cinema-gold mb-6">Contact</h4>
              <ul className="space-y-4 text-sm text-cinema-muted font-sans">
                <li className="flex items-center gap-3 min-w-0">
                  <Mail className="w-4 h-4 text-cinema-gold/60 shrink-0" />
                  <a href={`mailto:${contactEmail}`} className="hover:text-white transition-colors break-all text-xs lg:text-sm max-w-full">{contactEmail}</a>
                </li>
                <li className="flex items-center gap-3">
                  <Phone className="w-4 h-4 text-cinema-gold/60" />
                  <a href="tel:+256706739311" className="hover:text-white transition-colors">+256 706 739 311</a>
                </li>
                <li className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-cinema-gold/60 mt-0.5" />
                  <span>Kampala, Uganda</span>
                </li>
              </ul>
            </div>

            <div className="sm:pl-6 lg:pl-12">
              <h4 className="text-xs uppercase tracking-wider text-cinema-gold mb-6">Follow</h4>
              <div className="flex gap-4">
                {[
                  { Icon: Instagram, href: 'https://instagram.com/synergymultimediaacademy' },
                  { Icon: Youtube, href: 'https://youtube.com/synergymultimediaacademy' },
                  { Icon: Facebook, href: 'https://facebook.com/synergymultimediaacademy' },
                ].map(({ Icon, href }) => (
                  <a
                    key={href}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 cinema-glass rounded-none text-cinema-muted hover:text-cinema-gold transition-colors"
                  >
                    <Icon className="w-4 h-4" />
                  </a>
                ))}
              </div>
            </div>
          </div>

          <div className="cinema-divider mb-8" />
          <p className="text-center text-xs text-cinema-muted/60 font-sans tracking-wide">
            © {new Date().getFullYear()} Synergy Multimedia Academy. All rights reserved.
          </p>
        </div>
      </footer>
    </>
  )
}
